import { useMemo, useState } from "react";
import { AlertCircle, CheckCircle2, MinusCircle, Search, X } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Professional } from "@/pages/professionals/types/professional";
import { ProfessionalStatus } from "../schema/professional-schema";
import { useInviteProfessionalsBulk } from "../hooks/use-invite-professionals-bulk";
import type { BulkInviteResponse } from "../services/invite-professionals-bulk";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  professionals: Professional[];
  isLoading?: boolean;
};

/** Why a professional cannot be invited, or null if they can. */
function ineligibleReason(p: Professional): string | null {
  if (p.clerk_id) return "already has access";
  if (p.status === ProfessionalStatus.FULLY_DISMISSED) return "fully dismissed";
  if (!p.email?.trim()) return "no email address";
  return null;
}

/**
 * Bulk "invite to the professional dashboard".
 *
 * Lists only the professionals who can actually be invited, with a line saying
 * how many were left out and why — hiding them silently would leave an admin
 * hunting for a name that is deliberately absent.
 *
 * Nothing is selected when it opens. These are real invitation emails to real
 * clinicians and there is no unsend, so an accidental Enter should send nothing
 * rather than sixty.
 *
 * After sending, the dialog stays open and shows a per-person result. Partial
 * failure is the normal case — some addresses already have a Clerk account —
 * and a summary toast would bury exactly the rows worth acting on.
 */
export default function BulkInviteDialog({
  open,
  onOpenChange,
  professionals,
  isLoading,
}: Props) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [result, setResult] = useState<BulkInviteResponse | null>(null);
  const { mutate, isPending } = useInviteProfessionalsBulk();

  const { invitable, excluded } = useMemo(() => {
    const invitable: Professional[] = [];
    const excluded: Record<string, number> = {};
    for (const p of professionals) {
      const reason = ineligibleReason(p);
      if (reason) excluded[reason] = (excluded[reason] ?? 0) + 1;
      else invitable.push(p);
    }
    invitable.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    return { invitable, excluded };
  }, [professionals]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return invitable;
    return invitable.filter(
      (p) =>
        p.name?.toLowerCase().includes(needle) ||
        p.email?.toLowerCase().includes(needle)
    );
  }, [invitable, search]);

  // "Select all" acts on what is currently VISIBLE, not the whole roster —
  // after a search, selecting all should mean the matches, not silently add
  // everyone else too.
  const allVisibleSelected =
    visible.length > 0 && visible.every((p) => selected.has(p.id));

  const reset = () => {
    setSelected(new Set());
    setSearch("");
    setResult(null);
  };

  const close = () => {
    onOpenChange(false);
    // Leave the result on screen through the closing animation.
    setTimeout(reset, 200);
  };

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const excludedSummary = Object.entries(excluded)
    .map(([reason, count]) => `${count} ${reason}`)
    .join(", ");

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {result ? "Invitations sent" : "Invite to Professional Dashboard"}
          </DialogTitle>
          <DialogDescription>
            {result
              ? "Each professional below was handled separately. Anything that failed can be retried."
              : "They receive an email invitation. Their access is set up when they accept it."}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <ResultView result={result} />
        ) : (
          <>
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or email…"
                  className="pl-8"
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={visible.length === 0}
                onClick={() =>
                  setSelected((prev) => {
                    const next = new Set(prev);
                    if (allVisibleSelected) visible.forEach((p) => next.delete(p.id));
                    else visible.forEach((p) => next.add(p.id));
                    return next;
                  })
                }
              >
                {allVisibleSelected ? "Clear" : "Select all"}
              </Button>
            </div>

            <p className="text-xs text-gray-500">
              {isLoading
                ? "Loading professionals…"
                : `${invitable.length} can be invited.` +
                  (excludedSummary ? ` ${excludedSummary} — not shown.` : "")}
            </p>

            <ScrollArea className="h-[340px] rounded-md border border-gray-200">
              {visible.length === 0 ? (
                <p className="p-6 text-center text-sm text-gray-500">
                  {isLoading
                    ? "Loading…"
                    : search.trim()
                    ? `Nobody matches "${search.trim()}".`
                    : "There is nobody left to invite."}
                </p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {visible.map((p) => (
                    <li key={p.id}>
                      <label className="flex cursor-pointer items-center gap-3 px-4 py-2.5 hover:bg-gray-50">
                        <Checkbox
                          checked={selected.has(p.id)}
                          onCheckedChange={() => toggle(p.id)}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm text-gray-900">
                            {p.name}
                          </span>
                          <span className="block truncate text-xs text-gray-500">
                            {p.email}
                          </span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </ScrollArea>
          </>
        )}

        <DialogFooter>
          {result ? (
            <Button type="button" onClick={close}>
              Done
            </Button>
          ) : (
            <>
              <Button type="button" variant="outline" onClick={close} disabled={isPending}>
                Cancel
              </Button>
              <Button
                type="button"
                disabled={selected.size === 0 || isPending}
                onClick={() =>
                  mutate([...selected], { onSuccess: (data) => setResult(data) })
                }
              >
                {isPending
                  ? `Sending ${selected.size}…`
                  : selected.size === 0
                  ? "Select someone to invite"
                  : `Invite ${selected.size} ${selected.size === 1 ? "person" : "people"}`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResultView({ result }: { result: BulkInviteResponse }) {
  // Failures first — they are the only rows that need anyone to do anything.
  const order = { failed: 0, skipped: 1, invited: 2 } as const;
  const rows = [...result.results].sort(
    (a, b) => order[a.outcome] - order[b.outcome]
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-4 text-sm">
        <span className="flex items-center gap-1.5 text-green-700">
          <CheckCircle2 className="h-4 w-4" /> {result.invited} invited
        </span>
        <span className="flex items-center gap-1.5 text-gray-500">
          <MinusCircle className="h-4 w-4" /> {result.skipped} skipped
        </span>
        <span className="flex items-center gap-1.5 text-red-600">
          <X className="h-4 w-4" /> {result.failed} failed
        </span>
      </div>

      <ScrollArea className="h-[300px] rounded-md border border-gray-200">
        <ul className="divide-y divide-gray-100">
          {rows.map((r) => (
            <li key={r.professional_id} className="flex items-start gap-3 px-4 py-2.5">
              {r.outcome === "invited" ? (
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
              ) : r.outcome === "skipped" ? (
                <MinusCircle className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
              ) : (
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-gray-900">
                  {r.name ?? r.professional_id}
                </span>
                <span className="block truncate text-xs text-gray-500">
                  {r.reason ?? r.email}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </ScrollArea>
    </div>
  );
}
